import type { ReactNode } from 'react';
import { Paper, Group, Text, ThemeIcon, Badge, Stack } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconRefresh } from '@tabler/icons-react';
import type { TablerIcon } from '@tabler/icons-react';

type WidgetNodeShellProps = {
  icon: TablerIcon;
  title: string;
  subtitle?: string;
  configured?: boolean;
  errors?: string[];
  selected?: boolean;
  color?: string;
  minWidth?: number;
  minHeight?: number;
  retryCount?: number;
  children: ReactNode;
};

export function WidgetNodeShell({
  icon: Icon,
  title,
  subtitle,
  configured = false,
  errors = [],
  selected = false,
  color = 'indigo',
  minWidth = 180,
  minHeight,
  retryCount,
  children,
}: WidgetNodeShellProps) {
  const hasErrors = errors.length > 0;

  return (
    <Paper
      p="sm"
      radius="md"
      style={{
        minWidth,
        minHeight,
        border: selected
          ? '2px solid var(--mantine-primary-color-filled)'
          : hasErrors
            ? '2px solid var(--mantine-color-red-6)'
            : '1px solid var(--mantine-color-default-border)',
        boxShadow: selected ? '0 0 12px var(--mantine-primary-color-light-hover)' : undefined,
        background: 'var(--mantine-color-body)',
        position: 'relative',
      }}
    >
      <Stack gap="xs">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" size="lg" color={hasErrors ? 'red' : color} radius="md">
            <Icon size={18} />
          </ThemeIcon>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Text size="sm" fw={600} truncate>
                {title}
              </Text>
              {hasErrors ? (
                <Badge size="xs" color="red" variant="light" leftSection={<IconAlertCircle size={10} />}>
                  {errors.length}
                </Badge>
              ) : configured ? (
                <ThemeIcon size="xs" color="green" variant="light" radius="xl">
                  <IconCheck size={10} />
                </ThemeIcon>
              ) : null}
              {retryCount !== undefined && retryCount > 0 && (
                <Badge size="xs" color="orange" variant="light" leftSection={<IconRefresh size={10} />}>
                  {retryCount}
                </Badge>
              )}
            </Group>
            {subtitle && (
              <Text size="xs" c="dimmed" truncate>
                {subtitle}
              </Text>
            )}
          </div>
        </Group>
      </Stack>
      {children}
    </Paper>
  );
}
